const DEFAULT_SPEED = 60;
const MIN_COPIES = 2;
const MAX_RENDERED_ITEMS = 240;
const TICK_INTERVAL_MS = 1000;
const LAYOUT_EPSILON_PX = 1;

const reducedMotionQuery =
    typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;

function prefersReducedMotion() {
    return Boolean(reducedMotionQuery?.matches);
}

function escapeAttribute(value) {
    return typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(value)
        : value.replace(/["\\]/g, "\\$&");
}

function parseLabels(dataset) {
    return {
        days: dataset.announcementLabelDays || "d",
        hours: dataset.announcementLabelHours || "h",
        minutes: dataset.announcementLabelMinutes || "m",
        seconds: dataset.announcementLabelSeconds || "s",
    };
}

function pad(value) {
    return value < 10 ? `0${value}` : String(value);
}

function formatRemaining(totalSeconds, labels) {
    const total = Math.max(0, totalSeconds);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    if (days > 0) {
        return `${days}${labels.days} ${pad(hours)}${labels.hours} ${pad(minutes)}${labels.minutes} ${pad(seconds)}${labels.seconds}`;
    }

    if (hours > 0) {
        return `${pad(hours)}${labels.hours} ${pad(minutes)}${labels.minutes} ${pad(seconds)}${labels.seconds}`;
    }

    return `${pad(minutes)}${labels.minutes} ${pad(seconds)}${labels.seconds}`;
}

function renderValue(node, value) {
    const fragment = document.createDocumentFragment();
    let buffer = "";

    for (const char of value) {
        if (char < "0" || char > "9") {
            buffer += char;
            continue;
        }

        if (buffer) {
            fragment.appendChild(document.createTextNode(buffer));
            buffer = "";
        }

        const digit = document.createElement("span");
        digit.className = "announcement-bar__countdown-digit";
        digit.textContent = char;
        fragment.appendChild(digit);
    }

    if (buffer) fragment.appendChild(document.createTextNode(buffer));

    node.textContent = "";
    node.appendChild(fragment);
}

class AnnouncementBar {
    constructor(root) {
        this.root = root;
        this.viewport = root.querySelector("[data-announcement-viewport]");
        this.track = root.querySelector("[data-announcement-track]");
        this.source = this.track
            ? this.track.querySelector("[data-announcement-clone]")
            : null;
        this.speed =
            Number.parseFloat(root.dataset.announcementSpeed) || DEFAULT_SPEED;
        this.labels = parseLabels(root.dataset);
        this.preview = root.hasAttribute("data-announcement-preview");
        this.countdowns = [];
        this.timerId = null;
        this.frameId = null;
        this.observers = [];
        this.destroyed = false;
        this.lastCopies = null;
        this.lastGroupWidth = null;

        this.handleMotionChange = this.handleMotionChange.bind(this);
        this.tick = this.tick.bind(this);

        this.collectCountdowns();
        this.tick();

        if (this.destroyed) return;

        this.layout();
        this.lockScroll();
        this.observeSize();
        this.observeVisibility();
        this.observeFonts();

        if (reducedMotionQuery) {
            if (typeof reducedMotionQuery.addEventListener === "function") {
                reducedMotionQuery.addEventListener(
                    "change",
                    this.handleMotionChange,
                );
            } else if (typeof reducedMotionQuery.addListener === "function") {
                reducedMotionQuery.addListener(this.handleMotionChange);
            }
        }

        this.startTimer();
    }

    groups() {
        return this.track
            ? Array.from(
                  this.track.querySelectorAll("[data-announcement-group]"),
              )
            : [];
    }

    layout() {
        if (this.destroyed || !this.viewport || !this.track) return;

        const primary = this.groups()[0];
        if (!primary) return;

        if (prefersReducedMotion()) {
            this.setCopies(MIN_COPIES);
            this.track.style.removeProperty("--announcement-copies");
            this.track.style.removeProperty("--announcement-distance");
            this.track.style.removeProperty("--announcement-duration");
            this.lastCopies = null;
            this.lastGroupWidth = null;
            return;
        }

        const groupWidth = primary.getBoundingClientRect().width;
        const viewportWidth = this.viewport.getBoundingClientRect().width;
        if (groupWidth <= 0 || viewportWidth <= 0) return;

        const itemsPerGroup = Math.max(
            1,
            primary.querySelectorAll("[data-announcement-item]").length,
        );
        const maxCopies = Math.max(
            MIN_COPIES,
            Math.floor(MAX_RENDERED_ITEMS / itemsPerGroup),
        );
        const copies = Math.min(
            maxCopies,
            Math.max(MIN_COPIES, Math.ceil(viewportWidth / groupWidth) + 1),
        );

        if (
            copies === this.lastCopies &&
            this.lastGroupWidth !== null &&
            Math.abs(groupWidth - this.lastGroupWidth) < LAYOUT_EPSILON_PX
        ) {
            return;
        }

        this.lastCopies = copies;
        this.lastGroupWidth = groupWidth;

        this.setCopies(copies);
        this.track.style.setProperty("--announcement-copies", String(copies));
        this.track.style.setProperty(
            "--announcement-distance",
            `${groupWidth.toFixed(3)}px`,
        );
        this.track.style.setProperty(
            "--announcement-duration",
            `${(groupWidth / this.speed).toFixed(3)}s`,
        );
    }

    scheduleLayout() {
        if (this.destroyed || this.frameId !== null) return;

        this.frameId = window.requestAnimationFrame(() => {
            this.frameId = null;
            this.layout();
        });
    }

    setCopies(count) {
        if (!this.track || !this.source) return;

        const groups = this.groups();

        for (let index = groups.length - 1; index >= count; index -= 1) {
            groups[index].remove();
        }

        if (groups.length >= count) return;

        const fragment = document.createDocumentFragment();
        for (let index = groups.length; index < count; index += 1) {
            fragment.appendChild(this.source.cloneNode(true));
        }
        this.track.appendChild(fragment);
    }

    collectCountdowns() {
        const seen = new Set();
        this.countdowns = [];

        for (const node of this.root.querySelectorAll(
            "[data-announcement-countdown]",
        )) {
            const blockId = node.dataset.blockId;
            const end = Number.parseInt(node.dataset.end, 10);

            if (!blockId || seen.has(blockId) || !Number.isFinite(end))
                continue;

            seen.add(blockId);
            this.countdowns.push({
                blockId,
                endMs: end * 1000,
                behaviour: node.dataset.behaviour || "hide_timer",
            });
        }
    }

    startTimer() {
        if (this.timerId !== null || !this.countdowns.length) return;
        this.timerId = window.setInterval(this.tick, TICK_INTERVAL_MS);
    }

    stopTimer() {
        if (this.timerId === null) return;
        window.clearInterval(this.timerId);
        this.timerId = null;
    }

    tick() {
        if (this.destroyed) return;

        const now = Date.now();
        const active = [];
        let structuralChange = false;

        for (const countdown of this.countdowns) {
            const remaining = Math.floor((countdown.endMs - now) / 1000);

            if (remaining > 0) {
                this.renderCountdown(countdown.blockId, remaining);
                active.push(countdown);
                continue;
            }

            if (this.expire(countdown)) structuralChange = true;
            if (this.destroyed) return;
        }

        this.countdowns = active;

        if (structuralChange) this.scheduleLayout();
        if (!this.countdowns.length) this.stopTimer();
    }

    renderCountdown(blockId, remaining) {
        const value = formatRemaining(remaining, this.labels);
        const selector = `[data-announcement-countdown][data-block-id="${escapeAttribute(blockId)}"] [data-announcement-countdown-value]`;

        for (const node of this.root.querySelectorAll(selector)) {
            if (node.textContent !== value) renderValue(node, value);
        }
    }

    expire(countdown) {
        const { blockId, behaviour } = countdown;
        const id = escapeAttribute(blockId);

        if (behaviour === "hide_banner" && !this.preview) {
            this.hideBar();
            return false;
        }

        if (behaviour === "hide_message" && !this.preview) {
            this.removeNodes(`[data-announcement-item][data-block-id="${id}"]`);
            this.removeNodes(
                `[data-announcement-separator][data-block-id="${id}"]`,
            );

            if (!this.root.querySelector("[data-announcement-item]")) {
                this.hideBar();
                return false;
            }

            return true;
        }

        if (behaviour === "expired_message") {
            for (const node of this.root.querySelectorAll(
                `[data-announcement-text][data-block-id="${id}"]`,
            )) {
                const template = node
                    .closest("[data-announcement-item]")
                    ?.querySelector("template[data-announcement-expired]");
                if (!template) continue;

                node.textContent = "";
                node.appendChild(template.content.cloneNode(true));
            }
        }

        this.removeNodes(
            `[data-announcement-countdown][data-block-id="${id}"]`,
        );
        return true;
    }

    removeNodes(selector) {
        for (const node of this.root.querySelectorAll(selector)) {
            node.remove();
        }
    }

    hideBar() {
        this.root.hidden = true;
        this.destroy();
    }

    lockScroll() {
        if (!this.viewport) return;

        this.viewport.addEventListener(
            "scroll",
            () => {
                if (prefersReducedMotion() || this.viewport.scrollLeft === 0) {
                    return;
                }
                this.viewport.scrollLeft = 0;
            },
            { passive: true },
        );
    }

    observeSize() {
        if (typeof ResizeObserver !== "function" || !this.viewport) return;

        const observer = new ResizeObserver(() => this.scheduleLayout());
        observer.observe(this.viewport);

        this.observers.push(observer);
    }

    observeVisibility() {
        if (typeof IntersectionObserver !== "function") return;

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    this.root.classList.toggle(
                        "announcement-bar--offscreen",
                        !entry.isIntersecting,
                    );
                }
            },
            { threshold: 0 },
        );

        observer.observe(this.root);
        this.observers.push(observer);
    }

    observeFonts() {
        if (
            !document.fonts ||
            typeof document.fonts.ready?.then !== "function"
        ) {
            return;
        }

        document.fonts.ready.then(() => this.scheduleLayout());
    }

    handleMotionChange() {
        this.scheduleLayout();
    }

    setPaused(paused) {
        this.root.classList.toggle("announcement-bar--paused", paused);
    }

    destroy() {
        this.destroyed = true;
        this.stopTimer();

        if (this.frameId !== null) {
            window.cancelAnimationFrame(this.frameId);
            this.frameId = null;
        }

        for (const observer of this.observers) {
            observer.disconnect();
        }
        this.observers = [];

        if (reducedMotionQuery) {
            if (typeof reducedMotionQuery.removeEventListener === "function") {
                reducedMotionQuery.removeEventListener(
                    "change",
                    this.handleMotionChange,
                );
            } else if (
                typeof reducedMotionQuery.removeListener === "function"
            ) {
                reducedMotionQuery.removeListener(this.handleMotionChange);
            }
        }
    }
}

const instances = new WeakMap();

function bars(scope) {
    const target = scope?.querySelectorAll ? scope : document;
    return Array.from(target.querySelectorAll("[data-announcement-bar]"));
}

function init(scope) {
    for (const root of bars(scope)) {
        if (instances.has(root)) continue;
        instances.set(root, new AnnouncementBar(root));
    }
}

function teardown(scope) {
    for (const root of bars(scope)) {
        const instance = instances.get(root);
        if (!instance) continue;
        instance.destroy();
        instances.delete(root);
    }
}

function setPaused(scope, paused) {
    const roots = bars(scope);
    const closest = scope?.closest?.("[data-announcement-bar]");
    if (closest && !roots.includes(closest)) roots.push(closest);

    for (const root of roots) {
        instances.get(root)?.setPaused(paused);
    }
}

init();

document.addEventListener("shopify:section:load", (event) =>
    init(event.target),
);
document.addEventListener("shopify:section:unload", (event) =>
    teardown(event.target),
);
document.addEventListener("shopify:block:select", (event) =>
    setPaused(event.target, true),
);
document.addEventListener("shopify:block:deselect", (event) =>
    setPaused(event.target, false),
);
